/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import * as React from 'react';
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';
import { Button, Dropdown, Input, Menu, Space, Typography, Select, Spin } from 'antd';
import type { InputRef } from 'antd';
import {
  CloudUploadOutlined,
  DownOutlined,
  FileAddOutlined,
  SaveOutlined,
  CopyOutlined,
  PauseOutlined,
  AudioOutlined,
  StopOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

import transcript_pb from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import HelpSegment from './HelpSegment';
import KeyboardUtils from '../keyboard-utils';
import { LanguageCodeOption } from '../speech';
import './EditorHeaderControls.css';
import ExportTranscriptModal from './ExportTranscriptModal';
import { BasicError, TranscriptExportFormat } from '../types';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

interface Props {
  onMetadataValueUpdate: (value: { [key: string]: any }) => void;
  onLanguageChange: (e: any, target: any) => void;
  canSave: boolean;
  languageCodes: LanguageCodeOption[];
  isDictation: boolean;
  selectedLanguageCode: LanguageCodeOption['value'];
  keyboardUtils: KeyboardUtils;
  recording?: boolean;
  metadata?: transcript_pb.TranscriptMetadata.AsObject;
  playing?: boolean;
  started?: boolean;
  stopped?: boolean;
  saved?: boolean;
  saving?: boolean;
  onStart: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onSave: () => void;
  onExport: (format: TranscriptExportFormat, noDownload?: boolean) => void;
  onRestart: () => void;
  transcriptPb?: transcript_pb.Transcript.AsObject;
  version?: transcript_pb.Transcript.Version.AsObject;
  waitingForExport: boolean;
  error?: BasicError;
  dismissError: () => void;
  waitingForSaveEdits?: boolean;
}

interface State {
  nameChanged: boolean;
  modalOpen: boolean;
}

class EditorHeaderControls extends React.Component<Props, State> {
  private inputRef: React.RefObject<InputRef>;
  constructor(props: Props) {
    super(props);

    this.state = {
      nameChanged: false,
      modalOpen: false,
    };

    this.inputRef = React.createRef<InputRef>();
  }

  onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const subject = event.currentTarget.value;
    this.props.onMetadataValueUpdate({ subject });
  };

  onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (this.inputRef.current) {
      this.inputRef.current.blur();
    }
  };

  isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  timestampToString = (ts?: Timestamp.AsObject): string => {
    if (!ts) {
      return '';
    }
    const date = new Date(ts.seconds * 1e3 + ts.nanos * 1e-6);

    if (this.isToday(date)) {
      return `${date.toLocaleTimeString('is', { hour12: false })} í dag`;
    } else {
      return dayjs(date).format('L LT');
    }
  };

  toggleModal = () => this.setState({ modalOpen: !this.state.modalOpen });

  componentDidUpdate(prevProps: Props) {
    if (prevProps.waitingForExport !== this.props.waitingForExport) {
      console.log('waitingForExport changed: ', this.props.waitingForExport);
    }
  }
  componentDidMount() {
    console.log('Initial value of waitingForExport: ', this.props.waitingForExport);
  }

  handleLanguageChange = (value: string) => {
    this.props.onLanguageChange(null, { value: value });
  };

  render() {
    const {
      keyboardUtils,
      languageCodes,
      isDictation,
      onExport,
      onPause,
      onRestart,
      onResume,
      onSave,
      canSave,
      onStart,
      onStop,
      metadata,
      recording,
      saved,
      saving,
      selectedLanguageCode,
      started,
      stopped,
      version,
      waitingForSaveEdits,
    } = this.props;

    const fileMenu = (
      <Menu>
        <Menu.Item key='new' disabled={!stopped || !saved || saving} onClick={onRestart} icon={<FileAddOutlined />}>
          Nýtt skjal
        </Menu.Item>
        <Menu.Item key='save' onClick={onSave} icon={<SaveOutlined />} disabled={!canSave}>
          Vista
        </Menu.Item>
        <Menu.Item key='copy' onClick={() => onExport('txt', true)} icon={<CopyOutlined />}>
          Afrita texta
        </Menu.Item>
      </Menu>
    );

    const languageOptions = languageCodes.map((lang) => (
      <Option key={lang.key} value={lang.value}>
        {lang.text}
      </Option>
    ));

    return (
      <Space direction='vertical' className={'editor-header-controls'} style={{ width: '100%' }}>
        <div className={'editor-header-controls-top-row'}>
          <div className={'editor-header-controls-input-container'}>
            <form onSubmit={this.onSubmit}>
              <Input
                type='text'
                onBlur={onSave}
                ref={this.inputRef}
                onChange={this.onNameChange}
                value={metadata ? metadata.subject : ''}
                placeholder={'Ónefnt skjal'}
                style={{ fontSize: '1.2rem', fontWeight: 500 }}
              />
            </form>
          </div>
          {version && (
            <Text type='secondary' className={'last-saved'}>
              <CloudUploadOutlined style={{ marginRight: '8px' }} />
              {waitingForSaveEdits ? (
                <>
                  Vista... <Spin spinning size='small' />
                </>
              ) : (
                `Síðast vistað: ${this.timestampToString(version.creationTime)}`
              )}
            </Text>
          )}
        </div>

        <Space.Compact className={'editor-header-controls-bottom-row'}>
          <Dropdown overlay={fileMenu}>
            <Button>
              Skjal <DownOutlined />
            </Button>
          </Dropdown>

          <Button onClick={this.toggleModal} icon={<DownloadOutlined />}>
            Niðurhal
          </Button>
          <ExportTranscriptModal
            isOpen={this.state.modalOpen}
            toggleModal={this.toggleModal}
            onExport={onExport}
            waitingForExport={this.props.waitingForExport}
            error={
              this.props.error &&
              this.props.error.type &&
              this.props.error.type === 'export' &&
              this.props.error.message
                ? this.props.error.message
                : undefined
            }
            dismissError={this.props.dismissError}
          />

          <HelpSegment keyboardUtils={keyboardUtils} />

          {isDictation && (
            <Button.Group>
              <Select
                value={selectedLanguageCode}
                onChange={this.handleLanguageChange}
                disabled={started}
                placeholder='Mál'
                style={{ width: 180 }}
              >
                {languageOptions}
              </Select>

              {started ? (
                recording ? (
                  <Button icon={<PauseOutlined />} onClick={onPause}>
                    Hlé
                  </Button>
                ) : (
                  <Button
                    icon={<AudioOutlined style={{ color: recording ? 'red' : 'grey' }} />}
                    onClick={onResume}
                    disabled={stopped}
                  >
                    {stopped ? null : 'Halda áfram'}
                  </Button>
                )
              ) : (
                <Button icon={<AudioOutlined />} onClick={onStart}>
                  Byrja
                </Button>
              )}
              <Button icon={<StopOutlined />} onClick={onStop} disabled={recording || !started || stopped}>
                {recording || !started || stopped ? null : 'Ljúka'}
              </Button>
            </Button.Group>
          )}
        </Space.Compact>
      </Space>
    );
  }
}

export default React.memo(EditorHeaderControls);
